import React, { useState, useRef, useEffect, useCallback } from "react";
import { ReactComponent as NotificationIcon } from "../../Assets/Main/SearchBar/notification.svg";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { ReactComponent as DropdownIcon } from "../../Assets/Main/Utils/Dropdown.svg";
import { logout } from "../../Redux/Actions/authActions";
import { ReactComponent as Logout } from "../../Global Icons/logout.svg";
import NotificationPopup from "../Main/notificationPopup";
import ChangePasswordPopup from "../ChangePassword/ChangePasword";
import {
  fetchNotifications,
  fetchReadNotifications,
  markNotificationsRead,
} from "../../Redux/Actions/authActions";
import MediaUploadPopup from "../Configurations/mediaUploadPopup";
import "./searchBar.css";

const SearchBar = () => {
  const { admin, isAuthenticated, payment_executive } = useSelector(
    (state) => state.admin
  );
  const unreadCount = useSelector(
    (state) => state.notification?.unreadCount ?? 0
  );
  const [readNotifs, setReadNotifs] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const loggedUser =
    isAuthenticated && admin?.data ? admin : payment_executive;

  const logoutFunction = (e) => {
    e.preventDefault();
    dispatch(logout());
    navigate("/");
  };

  const [isProfilePopupVisible, setIsProfilePopupVisible] = useState(false);
  const [isNotificationPopupVisible, setIsNotificationPopupVisible] =
    useState(false);

  // Unread notifications
  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const isLoadingRef = useRef(false);
  const notificationsRef = useRef([]);
  notificationsRef.current = notifications;

  // Read notifications (data lives in Redux, pagination tracked locally)
  const [readPage, setReadPage] = useState(1);
  const [hasMoreRead, setHasMoreRead] = useState(true);
  const isLoadingReadRef = useRef(false);

  const [isMarkingVisibleRead, setIsMarkingVisibleRead] = useState(false);
  const [prevUnreadCount, setPrevUnreadCount] = useState(0);
  const audioRef = useRef(null);
  const [isChangePasswordPopupOpen, setIsChangePasswordPopupOpen] =
    useState(false);

  const [isMediaPopupVisible, setIsMediaPopupVisible] = useState(false);

  const profilePopupRef = useRef(null);
  const notificationPopupRef = useRef(null);

  const isAdminOrSuper =
    loggedUser?.data?.role === "admin" ||
    loggedUser?.data?.role === "super_admin";

  // ── Poll every 5 min ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAdminOrSuper) return;

    const intervalId = setInterval(async () => {
      try {
        const response = await dispatch(fetchNotifications(1, 10, -1, false));
        if (response) {
          const unreadCountFromApi = response.total || 0;
          if (unreadCountFromApi > prevUnreadCount) {
            audioRef.current
              ?.play()
              .catch((err) => console.error("Audio play error:", err));
          }
          setPrevUnreadCount(unreadCountFromApi);
          dispatch({
            type: "UPDATE_UNREAD_COUNT",
            payload: unreadCountFromApi,
          });
        }
      } catch (error) {
        console.error("Error refreshing notifications:", error);
      }
    }, 300000);

    return () => clearInterval(intervalId);
  }, [dispatch, prevUnreadCount, loggedUser]);

  // ── Load unread notifications ────────────────────────────────────────────
  const loadNotifications = useCallback(
    async (pageNumber) => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;
      try {
        const alreadyUnread =
          pageNumber === 1
            ? 0
            : notificationsRef.current.filter((n) => !n.is_read).length;
        const serverPage = Math.floor(alreadyUnread / 10) + 1;
        const response = await dispatch(
          fetchNotifications(serverPage, 10, -1, false)
        );
        if (response) {
          const fetched = response.data || [];
          const existingIds = new Set(
            notificationsRef.current.map((n) => n._id)
          );
          const newNotifications =
            pageNumber === 1
              ? fetched
              : fetched.filter((n) => !existingIds.has(n._id));
          if (newNotifications.length === 0) {
            setHasMore(false);
            isLoadingRef.current = false;
            return;
          }
          setNotifications((prev) =>
            pageNumber === 1
              ? newNotifications
              : [...prev, ...newNotifications]
          );
          setPage(pageNumber);
          setHasMore(fetched.length >= 10);
          dispatch({
            type: "UPDATE_UNREAD_COUNT",
            payload: response.total || 0,
          });
        }
      } catch (error) {
        console.error(error);
      }
      isLoadingRef.current = false;
    },
    [dispatch]
  );

  const loadReadNotifications = useCallback(
    async (pageNumber) => {
      if (isLoadingReadRef.current) return;
      isLoadingReadRef.current = true;
      try {
        const response = await dispatch(
          fetchReadNotifications(pageNumber, 10, -1, true)
        );
        if (response) {
          const data = response.data || [];
          setReadNotifs((prev) =>
            pageNumber === 1 ? data : [...prev, ...data]
          );
          setReadPage(pageNumber);
          setHasMoreRead(data.length >= 10);
        }
      } catch (error) {
        console.error(error);
      }
      isLoadingReadRef.current = false;
    },
    [dispatch]
  );

  // ── Initial load ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (isAdminOrSuper) {
      loadNotifications(1);
    }
  }, [loadNotifications, loggedUser]);

  const loadMoreNotifications = () => {
    if (!hasMore) return;
    loadNotifications(page + 1);
  };

  const loadMoreReadNotifications = () => {
    if (!hasMoreRead) return;
    loadReadNotifications(readPage + 1);
  };

  // ── Mark visible as read ─────────────────────────────────────────────────
  const markVisibleNotificationsRead = async (visibleIds) => {
    if (isMarkingVisibleRead) return;
    if (!visibleIds || visibleIds.length === 0) return;
    setIsMarkingVisibleRead(true);
    try {
      const toMarkIds = visibleIds.filter((id) =>
        notifications.find((n) => n._id === id && !n.is_read)
      );
      if (toMarkIds.length === 0) {
        setIsMarkingVisibleRead(false);
        return;
      }
      await dispatch(markNotificationsRead(toMarkIds));
      setNotifications((prev) =>
        prev.map((notif) =>
          toMarkIds.includes(notif._id)
            ? { ...notif, is_read: true }
            : notif
        )
      );
      const newUnreadCount = Math.max(0, unreadCount - toMarkIds.length);
      dispatch({ type: "UPDATE_UNREAD_COUNT", payload: newUnreadCount });
    } catch (error) {
      console.error("Error marking visible notifications read:", error);
    }
    setIsMarkingVisibleRead(false);
  };

  // ── Toggle popups ─────────────────────────────────────────────────────────
  const toggleProfilePopup = () => {
    setIsProfilePopupVisible((prev) => !prev);
    if (isNotificationPopupVisible) setIsNotificationPopupVisible(false);
  };

  const toggleNotificationPopup = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const willShow = !isNotificationPopupVisible;
    setIsNotificationPopupVisible(willShow);
    if (willShow) {
      // Only fetch fresh if no data loaded yet
      if (notifications.length === 0) loadNotifications(1);
      if (readNotifs.length === 0) loadReadNotifications(1);
    }
    if (isProfilePopupVisible) setIsProfilePopupVisible(false);
  };

  const openChangePasswordPopup = () => {
    setIsChangePasswordPopupOpen(true);
    setIsProfilePopupVisible(false);
  };

  const closeChangePasswordPopup = () => {
    setIsChangePasswordPopupOpen(false);
  };

  // ── Close on outside click ────────────────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profilePopupRef.current &&
        !profilePopupRef.current.contains(event.target) &&
        notificationPopupRef.current &&
        !notificationPopupRef.current.contains(event.target)
      ) {
        setIsProfilePopupVisible(false);
        setIsNotificationPopupVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div>
      <div
        className="search-bar-container border-15 flex-container"
        style={{ background: "#F5F7FA" }}
      >
        {/* <div
          style={{ background: "#F5F7FA", display: "flex", color: "#F5F7FA" }}
        >
          bbdshgg
        </div> */}

        {/* ── Notification Bell ── */}
        {isAdminOrSuper && (
          <div
            style={{ position: "relative", marginLeft: "65%", display: "flex", alignItems: "center", gap: "10px" }}
            ref={notificationPopupRef}
          >
            {/* Upload */}
            <div
              onClick={() => setIsMediaPopupVisible(true)}
              title="Upload Media"
              style={{ width: 43, height: 40, flexShrink: 0, borderRadius: "45%", background: "white", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", border: "1px solid #ececec" }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>

            {/* Bell */}
            <div className="search-bar-icon border-15">
              <a
                href="/"
                onClick={toggleNotificationPopup}
                style={{ cursor: "pointer" }}
              >
                <NotificationIcon
                  style={{
                    marginTop: 4,
                    background: "white",
                    borderRadius: "50px",
                  }}
                  className="icon"
                />
              </a>

              {/* Unread badge */}
              <span
                style={{
                  position: "absolute",
                  top: "-4px",
                  right: "10px",
                  backgroundColor: unreadCount === 0 ? "#000000" : "#e0245e",
                  color: "white",
                  borderRadius: "100%",
                  padding: "2px 4px",
                  fontSize: "13px",
                  fontWeight: "700",
                  lineHeight: 1,
                  boxShadow: "0 0 4px rgba(0,0,0,0.3)",
                  minWidth: 22,
                  height: 22,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  userSelect: "none",
                  pointerEvents: "none",
                  zIndex: 10,
                }}
              >
                {unreadCount}
              </span>
            </div>

            {/* Popup anchored below bell */}
            {isNotificationPopupVisible && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 10px)",
                  right: 0,
                  zIndex: 1000,
                }}
              >
                <NotificationPopup
                  visible={isNotificationPopupVisible}
                  notifications={notifications}
                  readNotifications={readNotifs}
                  loadMoreNotifications={loadMoreNotifications}
                  loadMoreReadNotifications={loadMoreReadNotifications}
                  hasMore={hasMore}
                  hasMoreRead={hasMoreRead}
                  markVisibleRead={markVisibleNotificationsRead}
                  unreadCount={unreadCount}
                />
              </div>
            )}
          </div>
        )}

        <div className="search-bar-flex-spacer"></div>

        {/* ── Profile ── */}
        <div
          className="search-bar-profile"
          style={{ background: "white", width: "100%", borderRadius: "24px" }}
        >
          <div
            className="flex-container font-Medium flex-row flex-align-center flex-justify-between profile border-24"
            onClick={toggleProfilePopup}
            style={{ cursor: "pointer" }}
          >
            <div className="flex-container flex-align-center flex-row align-centre">
              <img
                src={
                  isAuthenticated && loggedUser?.data?.profile_image
                    ? loggedUser.data.profile_image
                    : require("../../Assets/Main/SearchBar/Profile.png")
                }
                alt="Profile"
              />
              <p
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "53px",
                }}
              >
                {isAuthenticated && loggedUser?.data?.name
                  ? loggedUser.data.name
                  : "James"}
              </p>
            </div>
            <div>
              <DropdownIcon className="drop-icon" />
            </div>
          </div>
        </div>
      </div>

      {/* ── Profile Popup ── */}
      {isProfilePopupVisible && (
        <div className="popup" ref={profilePopupRef}>
          <div className="details_admin">
            <p style={{ fontFamily: "Gilroy-ExtraBold" }}>
              {isAuthenticated ? loggedUser.data.name : "No Data"}
            </p>
            <p style={{ color: "#c6c6c6" }}>
              {isAuthenticated ? loggedUser.data.email : "No Data"}
            </p>
          </div>
          <div className="profile_settings">
            {isAdminOrSuper && (
              <Link to="/settings">
                <div className="dropdown_flex">
                  <img
                    style={{ height: "20px", width: "30px" }}
                    src="https://mrproptekbucket.s3.ap-south-1.amazonaws.com/1750930878243_image_setting.svg"
                    alt=""
                  />
                  <p>Settings</p>
                </div>
              </Link>
            )}

            {isAuthenticated && loggedUser?.data?.role === "super_admin" && (
              <Link to="/permissions">
                <div className="dropdown_flex">
                  <img
                    style={{ height: "20px", width: "30px" }}
                    src="https://mrproptekbucket.s3.ap-south-1.amazonaws.com/1750930963173_image_Permissions.svg"
                    alt=""
                  />
                  <p>Permissions</p>
                </div>
              </Link>
            )}

            {isAuthenticated && loggedUser?.data?.role === "super_admin" && (
              <button
                onClick={openChangePasswordPopup}
                className="dropdown_flex"
                style={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                <img
                  style={{ height: "20px", width: "30px" }}
                  src="https://mrproptekbucket.s3.ap-south-1.amazonaws.com/1761887381746_image_PASSSWORD%20CHANGE.svg"
                  alt="Change Password"
                />
                <p style={{ fontWeight: "lighter", fontSize: "initial", color: "white" }}>
                  Change Password
                </p>
              </button>
            )}
          </div>

          <hr />

          <button
            onClick={logoutFunction}
            className="logout-button"
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              marginTop: "20px",
              border: "2px solid white"
            }}
          >
            <Logout
              style={{ width: "26px", height: "15px", marginTop: "17px" }}
            />
            <p style={{ marginRight: "14px" }}>Logout</p>
          </button>
        </div>
      )}

      {/* ── Change Password Popup ── */}
      {isChangePasswordPopupOpen && (
        <ChangePasswordPopup onClose={closeChangePasswordPopup} />
      )}

      <MediaUploadPopup
        visible={isMediaPopupVisible}
        onClose={() => setIsMediaPopupVisible(false)}
      />

      <audio
        ref={audioRef}
        src="../../Assets/notification/notification.mp3"
        preload="auto"
      />
    </div>
  );
};

export default SearchBar;